import { memo, RefObject, useCallback } from 'react';

import { useNavigate } from 'react-router';

import { ChevronRight } from '@mui/icons-material';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';

import { MenuNode } from 'components/header/menu-modules/types';
import { useBreakpoints } from 'hooks/useBreakpoints';

type MenuModulesItem = {
    node: MenuNode;
    onClose?: (node: MenuNode) => void;
    ref?: RefObject<HTMLLIElement | null>;
};

/**
 * Item de qualquer menu.
 * Caso tenha filhos, abre o submenu (exibe a seta à direita).
 * Caso contrário, navega para a página.
 */
export const MenuModulesItem = memo(function MenuModulesItem({ node, onClose, ref }: MenuModulesItem) {
    const { isUpMd } = useBreakpoints();
    const navigate = useNavigate();

    /**
     * Fecha o submenu e navega para a página clicada.
     */
    const handleClick = useCallback(() => {
        if (!onClose) return;

        const hasChildren = node.children && node.children.length > 0;

        if (hasChildren) {
            onClose(node);

            return;
        }

        if (!node.path) return;

        navigate(node.path);

        onClose(node);
    }, [node, navigate, onClose]);

    return (
        <MenuItem
            ref={ref}
            onClick={handleClick}
            sx={{
                'pointerEvents': 'auto',
                '&.MuiMenuItem-root': { minHeight: isUpMd ? 36 : 44 },
                ...(isUpMd && {
                    'borderLeft': '3px solid transparent',
                    '&:hover': {
                        'borderLeftColor': 'primary.main',
                        '& .MuiListItemText-primary': {
                            color: 'primary.main',
                            textShadow: '0 0 0.4px currentColor',
                        },
                        '& .MuiListItemIcon-root': { color: 'primary.main' },
                        '& .chevron-icon': { color: 'primary.main' },
                    },
                }),
                ...(!isUpMd && {
                    backgroundColor: 'grey.50',
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: 'grey.200',
                }),
            }}
        >
            {node.icon && <ListItemIcon>{node.icon}</ListItemIcon>}

            <ListItemText
                primary={node.title}
                secondary={node.breadcrumb}
                slotProps={{
                    primary: { sx: { whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.2, fontSize: isUpMd ? '14px' : '16px' } },
                    secondary: { sx: { color: 'text.disabled', fontSize: '11px' } },
                }}
            />

            {node.children && node.children.length > 0 && (
                <ChevronRight fontSize={isUpMd ? 'small' : 'medium'} className="chevron-icon" sx={{ ml: 1, color: 'text.secondary' }} />
            )}
        </MenuItem>
    );
});
